import styles from './coverage-threshold.module.scss';
import {
  Button,
  RadioButton,
  RadioButtonGroup,
  SkeletonText,
} from '@carbon/react';
import { Map as MapIcon } from '@carbon/react/icons';

interface ThresholdOption {
  label: string;
  value: number;
  isSelected?: boolean;
}

interface BaseProps {
  title: string;
  description: string;
  thresholdOptions: ThresholdOption[];
  instructions: string;
  isLoading: boolean;
  onThresholdChange: (threshold: number) => void;
}

type MapToggleProps =
  | { onShowOnMap: () => void; isMapActive: boolean }
  | { onShowOnMap?: undefined; isMapActive?: undefined };

type CoverageThresholdProps = BaseProps & MapToggleProps;

const slugify = (input: string): string =>
  input.toLowerCase().replace(/\s+/g, '-');

/**
 * Threshold radio controls with an optional "Show on Map" toggle.
 * Map rendering is the parent's responsibility — this component only emits
 * threshold-change and show-on-map events.
 *
 * `isMapActive` and `onShowOnMap` are paired (discriminated union): supply
 * both for an interactive toggle, or neither for a controls-only render.
 *
 * @param {CoverageThresholdProps} props - The component props.
 * @return {JSX.Element} The threshold controls.
 */
export const CoverageThreshold = (
  props: CoverageThresholdProps
): JSX.Element => {
  const {
    title,
    description,
    thresholdOptions,
    instructions,
    isLoading,
    onThresholdChange,
  } = props;

  const threshold = thresholdOptions.find((option) => option.isSelected)?.value;
  const isThresholdDisabled = threshold === undefined;
  const groupName = `coverage-threshold-${slugify(title)}`;

  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <h6>{title}</h6>
        {props.onShowOnMap && (
          <Button
            kind={props.isMapActive ? 'primary' : 'ghost'}
            size="sm"
            renderIcon={MapIcon}
            onClick={props.onShowOnMap}
            disabled={isLoading}
          >
            Show on Map
          </Button>
        )}
      </div>
      <p className={styles.description}>{description}</p>
      <div className={styles.thresholdControlContainer}>
        {isLoading ? (
          <SkeletonText
            data-testid="coverage-threshold-radio-skeleton"
            className={styles.radioSkeleton}
          />
        ) : (
          <RadioButtonGroup
            name={groupName}
            valueSelected={threshold ?? ''}
            disabled={isThresholdDisabled}
            onChange={(value) => {
              if (typeof value === 'number') onThresholdChange(value);
            }}
            legendText="Threshold Percentage"
          >
            {thresholdOptions.map((option) => (
              <RadioButton
                key={option.value}
                value={option.value}
                labelText={option.label}
              />
            ))}
          </RadioButtonGroup>
        )}
        <p className={styles.instructions}>{instructions}</p>
      </div>
    </div>
  );
};
